import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import CodeProtection from './CodeProtection';

const API_URL = 'https://leesgift.phantrium.com/api';

// Loading Screen Component
function LoadingScreen() {
  return (
    <div className="loading-screen">
      <div className="loading-content">
        <div className="loading-icon">
          <i className="fas fa-book-open"></i>
        </div>
        <div className="loading-text">De LeesGift wordt geladen...</div>
        <div className="loading-dots">
          <div className="loading-dot"></div>
          <div className="loading-dot"></div>
          <div className="loading-dot"></div>
        </div>
      </div>
    </div>
  );
}

// Context voor authenticatie
const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate initial loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (token) {
      // Verify token
      fetch(`${API_URL}/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        }
      })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setUser(data.data);
        } else {
          localStorage.removeItem('token');
          setToken(null);
        }
      })
      .catch(() => {
        localStorage.removeItem('token');
        setToken(null);
      });
    }
  }, [token]);

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      <CodeProtection />
      <Router>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/winkel" element={<ShopPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/*" element={<ProtectedAdmin><AdminDashboard /></ProtectedAdmin>} />
        </Routes>
      </Router>
    </AuthContext.Provider>
  );
}

// Protected Route Component
function ProtectedAdmin({ children }) {
  const { token } = React.useContext(AuthContext);
  return token ? children : <Navigate to="/admin/login" />;
}

// Home Page - Zakelijke informatie
function HomePage() {
  return (
    <div className="homepage">
      <header className="hero">
        <nav className="navbar">
          <div className="nav-content">
            <Link to="/" className="logo"><i className="fas fa-book"></i> De LeesGift</Link>
            <div className="nav-links">
              <Link to="/">Home</Link>
              <Link to="/winkel">Webshop</Link>
              <Link to="/admin/login">Admin</Link>
            </div>
          </div>
        </nav>

        <div className="hero-content">
          <h1>Welkom bij De LeesGift</h1>
          <p className="hero-subtitle">Dé online boekwinkel voor alle leesliefhebbers</p>
          <Link to="/winkel" className="cta-button">Bekijk onze collectie</Link>
        </div>
      </header>

      <section className="info-section">
        <div className="container">
          <h2>Over De LeesGift</h2>
          <div className="info-grid">
            <div className="info-card">
              <div className="info-icon"><i className="fas fa-book"></i></div>
              <h3>Breed Assortiment</h3>
              <p>Van bestsellers tot klassiekers, we hebben voor ieder wat wils. Ontdek ons uitgebreide aanbod aan boeken.</p>
            </div>
            <div className="info-card">
              <div className="info-icon"><i className="fas fa-truck"></i></div>
              <h3>Snelle Levering</h3>
              <p>Bestel vandaag, morgen in huis. Wij zorgen voor een snelle en betrouwbare levering van je bestelling.</p>
            </div>
            <div className="info-card">
              <div className="info-icon"><i className="fas fa-credit-card"></i></div>
              <h3>Veilig Betalen</h3>
              <p>Betaal veilig en gemakkelijk met iDEAL, creditcard of andere betaalmethoden van jouw voorkeur.</p>
            </div>
            <div className="info-card">
              <div className="info-icon">⭐</div>
              <h3>Klantenservice</h3>
              <p>Onze klantenservice staat altijd voor je klaar. Heb je vragen? Neem gerust contact met ons op.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Ontdek onze collectie</h2>
          <p>Duizenden boeken direct leverbaar</p>
          <Link to="/winkel" className="cta-button large">Naar de webshop</Link>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>De LeesGift</h4>
              <p>Dé online boekwinkel voor alle leesliefhebbers</p>
            </div>
            <div className="footer-section">
              <h4>Contact</h4>
              <p>Email: info@leesgift.nl</p>
              <p>Tel: 085 - 123 4567</p>
            </div>
            <div className="footer-section">
              <h4>Links</h4>
              <Link to="/winkel">Webshop</Link>
              <Link to="/admin/login">Admin Login</Link>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2025 De LeesGift - Ontwikkeld door <a href="https://phantrium.com" target="_blank" rel="noopener noreferrer">Phantrium</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Shop Page
function ShopPage() {
  const [books, setBooks] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCart, setShowCart] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBooks();
    // Load cart from localStorage
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  const fetchBooks = async () => {
    try {
      const res = await fetch(`${API_URL}/books`);
      const data = await res.json();
      setBooks(data.data?.data || []);
    } catch (error) {
      console.error('Error fetching books:', error);
    }
    setLoading(false);
  };

  const addToCart = (book) => {
    const existingItem = cart.find(item => item.id === book.id);
    let newCart;

    if (existingItem) {
      newCart = cart.map(item =>
        item.id === book.id ? { ...item, quantity: item.quantity + 1 } : item
      );
    } else {
      newCart = [...cart, { ...book, quantity: 1 }];
    }

    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const removeFromCart = (bookId) => {
    const newCart = cart.filter(item => item.id !== bookId);
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const updateQuantity = (bookId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(bookId);
      return;
    }

    const newCart = cart.map(item =>
      item.id === bookId ? { ...item, quantity } : item
    );
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (parseFloat(item.price) * item.quantity), 0).toFixed(2);
  };

  const filteredBooks = books.filter(book =>
    book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.author.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="shop-page">
      <nav className="navbar">
        <div className="nav-content">
          <Link to="/" className="logo"><i className="fas fa-book"></i> De LeesGift</Link>
          <div className="nav-links">
            <Link to="/">Home</Link>
            <Link to="/winkel">Webshop</Link>
            <button className="cart-button" onClick={() => setShowCart(!showCart)}>
              🛒 Winkelwagen ({cart.length})
            </button>
          </div>
        </div>
      </nav>

      <div className="shop-content">
        <div className="shop-header">
          <h1>Webshop</h1>
          <input
            type="text"
            placeholder="Zoek op titel of auteur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        {loading ? (
          <div className="loading">Laden...</div>
        ) : (
          <div className="books-grid">
            {filteredBooks.map(book => (
              <div key={book.id} className="book-card-shop">
                <div className="book-info">
                  <h3>{book.title}</h3>
                  <p className="author">door {book.author}</p>
                  {book.description && (
                    <p className="description">{book.description.substring(0, 100)}...</p>
                  )}
                  <div className="book-meta">
                    {book.isbn && <span>ISBN: {book.isbn}</span>}
                    {book.pages && <span>{book.pages} pagina's</span>}
                  </div>
                  <div className="book-footer">
                    <span className="price">€{book.price}</span>
                    {book.stock_quantity > 0 ? (
                      <button className="add-to-cart-btn" onClick={() => addToCart(book)}>
                        In winkelwagen
                      </button>
                    ) : (
                      <span className="out-of-stock">Niet op voorraad</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Shopping Cart Sidebar */}
      <div className={`cart-sidebar ${showCart ? 'open' : ''}`}>
        <div className="cart-header">
          <h2>Winkelwagen</h2>
          <button className="close-cart" onClick={() => setShowCart(false)}>×</button>
        </div>

        {cart.length === 0 ? (
          <p className="empty-cart">Je winkelwagen is leeg</p>
        ) : (
          <>
            <div className="cart-items">
              {cart.map(item => (
                <div key={item.id} className="cart-item">
                  <div className="cart-item-info">
                    <h4>{item.title}</h4>
                    <p>{item.author}</p>
                    <p className="item-price">€{item.price}</p>
                  </div>
                  <div className="cart-item-actions">
                    <div className="quantity-controls">
                      <button onClick={() => updateQuantity(item.id, item.quantity - 1)}>-</button>
                      <span>{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                    </div>
                    <button className="remove-btn" onClick={() => removeFromCart(item.id)}>
                      Verwijder
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="cart-footer">
              <div className="cart-total">
                <strong>Totaal:</strong>
                <strong>€{getTotalPrice()}</strong>
              </div>
              <button className="checkout-btn" onClick={() => navigate('/checkout')}>Afrekenen</button>
            </div>
          </>
        )}
      </div>

      {showCart && <div className="cart-overlay" onClick={() => setShowCart(false)} />}
    </div>
  );
}

// Checkout Page
function CheckoutPage() {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    country: 'Nederland'
  });

  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  const getTotalPrice = () => {
    const subtotal = cart.reduce((total, item) => total + (parseFloat(item.price) * item.quantity), 0);
    return subtotal.toFixed(2);
  };

  const getShippingCost = () => {
    const subtotal = parseFloat(getTotalPrice());
    return subtotal >= 25 ? 0 : 3.95;
  };

  const getTax = () => {
    const subtotal = parseFloat(getTotalPrice());
    return (subtotal * 0.09).toFixed(2);
  };

  const getGrandTotal = () => {
    const subtotal = parseFloat(getTotalPrice());
    const shipping = getShippingCost();
    const tax = parseFloat(getTax());
    return (subtotal + shipping + tax).toFixed(2);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        order_type: 'online',
        items: cart.map(item => ({
          book_id: item.id,
          quantity: item.quantity
        })),
        shipping_cost: getShippingCost()
      };

      const res = await fetch(`${API_URL}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(orderData)
      });

      const data = await res.json();

      if (data.success) {
        setOrderNumber(data.data.order_number);
        setOrderComplete(true);
        localStorage.removeItem('cart');
        setCart([]);
      } else {
        alert('Er is een fout opgetreden bij het plaatsen van je bestelling.');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Er is een fout opgetreden. Probeer het later opnieuw.');
    }

    setLoading(false);
  };

  if (orderComplete) {
    return (
      <div className="checkout-page">
        <nav className="navbar">
          <div className="nav-content">
            <Link to="/" className="logo"><i className="fas fa-book"></i> De LeesGift</Link>
          </div>
        </nav>

        <div className="checkout-content">
          <div className="order-success">
            <div className="success-icon">✓</div>
            <h1>Bestelling Geplaatst!</h1>
            <p>Bedankt voor je bestelling bij De LeesGift.</p>
            <p className="order-number">Bestelnummer: <strong>{orderNumber}</strong></p>
            <p>Je ontvangt binnen enkele minuten een bevestigingsmail met de orderdetails.</p>
            <div className="success-actions">
              <Link to="/winkel" className="btn-primary">Verder winkelen</Link>
              <Link to="/" className="btn-secondary">Naar homepage</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="checkout-page">
        <nav className="navbar">
          <div className="nav-content">
            <Link to="/" className="logo"><i className="fas fa-book"></i> De LeesGift</Link>
          </div>
        </nav>

        <div className="checkout-content">
          <div className="empty-checkout">
            <h1>Je winkelwagen is leeg</h1>
            <p>Voeg eerst enkele boeken toe aan je winkelwagen voordat je afrekent.</p>
            <Link to="/winkel" className="btn-primary">Naar de webshop</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <nav className="navbar">
        <div className="nav-content">
          <Link to="/" className="logo"><i className="fas fa-book"></i> De LeesGift</Link>
          <div className="nav-links">
            <Link to="/winkel">Terug naar winkel</Link>
          </div>
        </div>
      </nav>

      <div className="checkout-content">
        <h1>Afrekenen</h1>

        <div className="checkout-grid">
          <div className="checkout-form">
            <h2>Verzendgegevens</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Voornaam *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Achternaam *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Telefoonnummer *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group" style={{flex: 3}}>
                  <label>Straat *</label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group" style={{flex: 1}}>
                  <label>Huisnr. *</label>
                  <input
                    type="text"
                    name="houseNumber"
                    value={formData.houseNumber}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Postcode *</label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Plaats *</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Land *</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <button type="submit" className="submit-order-btn" disabled={loading}>
                {loading ? 'Bestelling plaatsen...' : 'Bestelling plaatsen'}
              </button>
            </form>
          </div>

          <div className="order-summary">
            <h2>Overzicht</h2>

            <div className="summary-items">
              {cart.map(item => (
                <div key={item.id} className="summary-item">
                  <div className="item-details">
                    <strong>{item.title}</strong>
                    <span>{item.quantity}x €{item.price}</span>
                  </div>
                  <div className="item-total">
                    €{(item.quantity * parseFloat(item.price)).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>

            <div className="summary-totals">
              <div className="summary-row">
                <span>Subtotaal</span>
                <span>€{getTotalPrice()}</span>
              </div>
              <div className="summary-row">
                <span>Verzendkosten</span>
                <span>{getShippingCost() === 0 ? 'Gratis' : `€${getShippingCost().toFixed(2)}`}</span>
              </div>
              <div className="summary-row">
                <span>BTW (9%)</span>
                <span>€{getTax()}</span>
              </div>
              <div className="summary-row total">
                <strong>Totaal</strong>
                <strong>€{getGrandTotal()}</strong>
              </div>
            </div>

            <div className="shipping-notice">
              {getShippingCost() > 0 && (
                <p>💡 Gratis verzending bij bestellingen vanaf €25</p>
              )}
              {getShippingCost() === 0 && (
                <p>✓ Gratis verzending</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Admin Login Page
function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = React.useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email, password })
      });

      const data = await res.json();

      if (data.success) {
        login(data.data.token, data.data.user);
        navigate('/admin/dashboard');
      } else {
        setError(data.message || 'Login mislukt');
      }
    } catch (err) {
      setError('Er is een fout opgetreden. Probeer het later opnieuw.');
    }

    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1><i className="fas fa-book"></i> De LeesGift</h1>
          <h2>Admin Login</h2>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="admin@leesgift.nl"
            />
          </div>

          <div className="form-group">
            <label>Wachtwoord</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
            />
          </div>

          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? 'Inloggen...' : 'Inloggen'}
          </button>
        </form>

        <div className="login-footer">
          <Link to="/">← Terug naar home</Link>
        </div>
      </div>
    </div>
  );
}

// Admin Dashboard
function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [books, setBooks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token, logout, user } = React.useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      };

      const statsRes = await fetch(`${API_URL}/stats`, { headers });
      const statsData = await statsRes.json();
      setStats(statsData);

      if (activeTab === 'books' || activeTab === 'dashboard') {
        const booksRes = await fetch(`${API_URL}/books`, { headers });
        const booksData = await booksRes.json();
        setBooks(booksData.data?.data || []);
      }

      if (activeTab === 'orders' || activeTab === 'dashboard') {
        const ordersRes = await fetch(`${API_URL}/orders`, { headers });
        const ordersData = await ordersRes.json();
        setOrders(ordersData.data?.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="header-content">
          <h1><i className="fas fa-book"></i> De LeesGift Admin</h1>
          <div className="header-actions">
            <span className="user-info">👤 {user?.name || user?.email}</span>
            <button onClick={handleLogout} className="logout-btn">Uitloggen</button>
          </div>
        </div>
      </header>

      <nav className="nav-tabs">
        <button
          className={activeTab === 'dashboard' ? 'active' : ''}
          onClick={() => setActiveTab('dashboard')}
        >
          📊 Dashboard
        </button>
        <button
          className={activeTab === 'books' ? 'active' : ''}
          onClick={() => setActiveTab('books')}
        >
          <i className="fas fa-book"></i> Boeken
        </button>
        <button
          className={activeTab === 'orders' ? 'active' : ''}
          onClick={() => setActiveTab('orders')}
        >
          📦 Bestellingen
        </button>
      </nav>

      <main className="main-content">
        {loading ? (
          <div className="loading">Laden...</div>
        ) : (
          <>
            {activeTab === 'dashboard' && <Dashboard stats={stats} books={books} orders={orders} />}
            {activeTab === 'books' && <BooksView books={books} />}
            {activeTab === 'orders' && <OrdersView orders={orders} />}
          </>
        )}
      </main>

      <footer className="app-footer">
        <p>© 2025 De LeesGift - Ontwikkeld door <a href="https://phantrium.com" target="_blank" rel="noopener noreferrer">Phantrium</a></p>
      </footer>
    </div>
  );
}

function Dashboard({ stats, books, orders }) {
  return (
    <div className="dashboard">
      <h2>Dashboard Overzicht</h2>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-book"></i></div>
          <div className="stat-info">
            <h3>Boeken</h3>
            <p className="stat-value">{stats?.books_count || 0}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-info">
            <h3>Klanten</h3>
            <p className="stat-value">{stats?.customers_count || 0}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📦</div>
          <div className="stat-info">
            <h3>Bestellingen</h3>
            <p className="stat-value">{stats?.orders_count || 0}</p>
          </div>
        </div>

        <div className="stat-card highlight">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <h3>Totale Omzet</h3>
            <p className="stat-value">€{stats?.total_revenue || '0.00'}</p>
          </div>
        </div>
      </div>

      <div className="dashboard-sections">
        <div className="section">
          <h3>Recent Toegevoegde Boeken</h3>
          <div className="items-list">
            {books.slice(0, 3).map(book => (
              <div key={book.id} className="item-card">
                <div className="item-info">
                  <strong>{book.title}</strong>
                  <p>{book.author}</p>
                  <span className="badge">€{book.price}</span>
                  <span className="badge stock">Voorraad: {book.stock_quantity}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="section">
          <h3>Recente Bestellingen</h3>
          <div className="items-list">
            {orders.slice(0, 3).map(order => (
              <div key={order.id} className="item-card">
                <div className="item-info">
                  <strong>{order.order_number}</strong>
                  <p>€{order.total_amount}</p>
                  <span className={`badge status-${order.status}`}>{order.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function BooksView({ books }) {
  const { token } = React.useContext(AuthContext);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [formData, setFormData] = useState({
    isbn: '',
    title: '',
    author: '',
    publisher: '',
    publication_year: '',
    category: '',
    description: '',
    price: '',
    cost_price: '',
    stock_quantity: '',
    min_stock: 5,
    language: 'Nederlands',
    pages: ''
  });

  const resetForm = () => {
    setFormData({
      isbn: '',
      title: '',
      author: '',
      publisher: '',
      publication_year: '',
      category: '',
      description: '',
      price: '',
      cost_price: '',
      stock_quantity: '',
      min_stock: 5,
      language: 'Nederlands',
      pages: ''
    });
    setEditingBook(null);
    setShowAddForm(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleEdit = (book) => {
    setEditingBook(book);
    setFormData({
      isbn: book.isbn || '',
      title: book.title || '',
      author: book.author || '',
      publisher: book.publisher || '',
      publication_year: book.publication_year || '',
      category: book.category || '',
      description: book.description || '',
      price: book.price || '',
      cost_price: book.cost_price || '',
      stock_quantity: book.stock_quantity || '',
      min_stock: book.min_stock || 5,
      language: book.language || 'Nederlands',
      pages: book.pages || ''
    });
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingBook
        ? `${API_URL}/books/${editingBook.id}`
        : `${API_URL}/books`;

      const method = editingBook ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (data.success) {
        alert(editingBook ? 'Boek bijgewerkt!' : 'Boek toegevoegd!');
        resetForm();
        window.location.reload();
      } else {
        alert('Er is een fout opgetreden.');
      }
    } catch (error) {
      console.error('Error saving book:', error);
      alert('Er is een fout opgetreden.');
    }
  };

  const handleDelete = async (book) => {
    if (!window.confirm(`Weet je zeker dat je "${book.title}" wilt verwijderen?`)) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/books/${book.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        }
      });

      const data = await res.json();

      if (data.success) {
        alert('Boek verwijderd!');
        window.location.reload();
      } else {
        alert('Er is een fout opgetreden.');
      }
    } catch (error) {
      console.error('Error deleting book:', error);
      alert('Er is een fout opgetreden.');
    }
  };

  return (
    <div className="books-view">
      <div className="view-header">
        <div>
          <h2>Boeken Inventaris</h2>
          <p>{books.length} boeken in voorraad</p>
        </div>
        <button
          className="add-book-btn"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? '✕ Annuleren' : '+ Nieuw Boek'}
        </button>
      </div>

      {showAddForm && (
        <div className="book-form-container">
          <h3>{editingBook ? 'Boek Bewerken' : 'Nieuw Boek Toevoegen'}</h3>
          <form onSubmit={handleSubmit} className="book-form">
            <div className="form-row">
              <div className="form-group">
                <label>ISBN *</label>
                <input
                  type="text"
                  name="isbn"
                  value={formData.isbn}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Titel *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Auteur *</label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Uitgever</label>
                <input
                  type="text"
                  name="publisher"
                  value={formData.publisher}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Publicatiejaar</label>
                <input
                  type="number"
                  name="publication_year"
                  value={formData.publication_year}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Categorie</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Beschrijving</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Verkoopprijs *</label>
                <input
                  type="number"
                  step="0.01"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Kostprijs</label>
                <input
                  type="number"
                  step="0.01"
                  name="cost_price"
                  value={formData.cost_price}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Voorraad *</label>
                <input
                  type="number"
                  name="stock_quantity"
                  value={formData.stock_quantity}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Min. Voorraad</label>
                <input
                  type="number"
                  name="min_stock"
                  value={formData.min_stock}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Taal</label>
                <input
                  type="text"
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Aantal pagina's</label>
                <input
                  type="number"
                  name="pages"
                  value={formData.pages}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-save">
                {editingBook ? 'Opslaan' : 'Toevoegen'}
              </button>
              <button type="button" className="btn-cancel" onClick={resetForm}>
                Annuleren
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="books-grid">
        {books.map(book => (
          <div key={book.id} className="book-card">
            <div className="book-header">
              <h3>{book.title}</h3>
              <span className="book-price">€{book.price}</span>
            </div>
            <div className="book-details">
              <p className="author">📝 {book.author}</p>
              {book.category && <p className="category">🏷️ {book.category}</p>}
              <p className="isbn">📘 ISBN: {book.isbn}</p>
              <p className="stock">
                📦 Voorraad: <strong>{book.stock_quantity}</strong> stuks
                {book.stock_quantity <= book.min_stock && (
                  <span className="low-stock"> ⚠️ Laag</span>
                )}
              </p>
              {book.description && (
                <p className="description">{book.description.substring(0, 100)}...</p>
              )}
            </div>
            <div className="book-meta">
              <span>{book.language}</span>
              {book.pages && <span>{book.pages} pagina's</span>}
              {book.publication_year && <span>{book.publication_year}</span>}
            </div>
            <div className="book-actions">
              <button className="btn-edit" onClick={() => handleEdit(book)}>
                Bewerken
              </button>
              <button className="btn-delete" onClick={() => handleDelete(book)}>
                Verwijderen
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersView({ orders }) {
  const getStatusColor = (status) => {
    const colors = {
      'pending': '#ffc107',
      'paid': '#28a745',
      'processing': '#17a2b8',
      'shipped': '#007bff',
      'completed': '#28a745',
      'cancelled': '#dc3545',
      'refunded': '#6c757d'
    };
    return colors[status] || '#666';
  };

  return (
    <div className="orders-view">
      <div className="view-header">
        <h2>Bestellingen</h2>
        <p>{orders.length} bestellingen</p>
      </div>

      <div className="orders-list">
        {orders.map(order => (
          <div key={order.id} className="order-card">
            <div className="order-header">
              <div>
                <h3>{order.order_number}</h3>
                <p className="order-date">
                  {new Date(order.created_at).toLocaleDateString('nl-NL', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <div className="order-amount">
                <strong>€{order.total_amount}</strong>
              </div>
            </div>

            <div className="order-details">
              <div className="order-badges">
                <span
                  className="badge"
                  style={{backgroundColor: getStatusColor(order.status)}}
                >
                  {order.status}
                </span>
                <span className="badge">{order.order_type}</span>
                <span className={`badge payment-${order.payment_status}`}>
                  {order.payment_status}
                </span>
              </div>

              {order.customer && (
                <p className="customer-info">
                  👤 Klant: {order.customer.first_name} {order.customer.last_name}
                </p>
              )}

              {order.shipping_city && (
                <p className="shipping-info">
                  📍 Verzending: {order.shipping_city}
                </p>
              )}
            </div>

            {order.items && order.items.length > 0 && (
              <div className="order-items">
                <strong>Items:</strong>
                <ul>
                  {order.items.map((item, index) => (
                    <li key={index}>
                      {item.quantity}x {item.title} - €{item.subtotal}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default App;
